import React, { useCallback, useMemo } from "react";
import useUserDetail from "@hooks/useUserDetail";
import AssetThumbnail from "@features/thumbnail/asset-thumbnail";
import CollectionThumbnail from "@features/thumbnail/collection-thumbnail";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import {
    Box,
    Button,
    Checkbox,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mzc-pdc/ui";
import StatusBadge from "@components_v2/status-badge/status-badge";
import TableCard from "@components_v2/card/table-card";
import { Link } from "react-router-dom";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import convertToByte from "@cores/convertToByte";
import { alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import convertUtcToLocal from "@cores/convertUtcToLocal";
import UserDetailPopper from "@features/user-detail-popper/user-detail-popper";
import { ARCHIVED_ASSET_STATUS, ASSET_PREVIEW_STATUS, MEDIA_TYPES, MENU_LIST } from "@constants";
import countDaysBetweenDates from "@cores/countDaysBetweentDates";
import moment from "moment";
import BadgeCollection from "@components_v2/badge-collection/badge-collection";
import { IconAi, IconDrm, IconLink, IconLock, IconStar, IconVoice } from "@mzc-cloudplex/icons";
import useSettings from "@hooks/useSettings";
import Tooltip from "@components_v2/Tooltip/tooltip";
import { canNavigateToArchiveDetail } from "@utils/archive-utils";

const TableList = ({
    keyPrefix,
    data,
    columns,
    selected,
    clicked,
    thumbnailCrop,
    type,
    actionButton,
    actionButtonProps,
    showColumns = false,
    disableHover,
    onClickName,
    onClickCheck,
    onClickRow,
    showAdditionalIcons = false,
}) => {
    const { t } = useTranslation();
    const {
        handleUserProfileClick,
        handleUserProfileClose,
        userProfileAnchorEl,
        openUserProfile,
        userProfileData,
        userProfilePending,
    } = useUserDetail();
    const { navigatorMenus } = useSettings({ type: "General" });

    const tableMinWidth = useMemo(() => {
        let totalWidth = 0;

        columns.forEach((column) => {
            if (column.id !== "name" && column.style?.custom?.width) {
                const width = column.style.custom.width;
                const numericWidth =
                    typeof width === "string"
                        ? parseInt(width.replace("px", ""))
                        : typeof width === "number"
                        ? width
                        : 0;
                totalWidth += numericWidth;
            }
        });

        totalWidth += 200;

        if (onClickCheck) {
            totalWidth += 32;
        }

        if (actionButton) {
            if (actionButtonProps?.width) {
                const actionWidth =
                    typeof actionButtonProps.width === "string"
                        ? parseInt(actionButtonProps.width.replace("px", ""))
                        : typeof actionButtonProps.width === "number"
                        ? actionButtonProps.width
                        : 50;
                totalWidth += actionWidth;
            } else {
                totalWidth += 50;
            }
        }

        return totalWidth;
    }, [columns, onClickCheck, actionButton, actionButtonProps]);

    const getNameThumbnail = useCallback(
        (type, option) => {
            switch (type) {
                case "asset":
                case "share_asset": {
                    let src;

                    const commonStatusPreviews = option?.previews?.filter(
                        (p) => p.status !== ASSET_PREVIEW_STATUS.ERROR,
                    );
                    const previewStatus =
                        commonStatusPreviews?.length === 0 && option.previews?.length > 0
                            ? ASSET_PREVIEW_STATUS.ERROR
                            : commonStatusPreviews?.[0]?.status ?? ASSET_PREVIEW_STATUS.COMPLETE;

                    if (commonStatusPreviews?.[0]?.accessUrl) src = commonStatusPreviews?.[0]?.accessUrl;
                    if (!src) {
                        if (option?.mediaType === MEDIA_TYPES.IMAGE) {
                            src = option.accessUrls?.[0] ?? option.elements?.[0]?.accessUrl;
                        } else if (option.mediaType === MEDIA_TYPES.VIDEO) src = option.thumbnails?.[0]?.accessUrl;
                    }

                    return (
                        <AssetThumbnail
                            variant={"table"}
                            src={src ?? ""}
                            minWidth={52}
                            width={52}
                            height={52}
                            thumbnailCrop={thumbnailCrop}
                            showIcons={type === "asset"}
                            asset={option}
                            disableHover={disableHover}
                            errorIconProps={{ position: "left", size: 16 }}
                            previewStatus={previewStatus}
                        />
                    );
                }
                case "collection":
                    return <CollectionThumbnail collection={option} size={"xsmall"} variant={"table"} />;
            }
        },
        [data],
    );

    const getNameIcon = useCallback(
        (type, option) => {
            switch (type) {
                case "asset":
                case "share_asset":
                    return <AssetTypeFlag size={"small"} type={option.mediaType} />;
            }
        },
        [data],
    );

    const getLinkComponent = useCallback(
        (type, item) => {
            if (
                type.toUpperCase() === "ASSET" &&
                canNavigateToArchiveDetail(item.archiveStatus) &&
                !navigatorMenus?.[MENU_LIST.ARCHIVED]?.visible
            ) {
                return (
                    <Tooltip
                        title={t(
                            `common::msg::It's not possible to proceed as the archived menu isn't being used.`,
                            `It's not possible to proceed as the archived menu isn't being used.`,
                        )}
                    >
                        <Box sx={{ width: "fit-content" }}>
                            <Typography variant={"body1_500"} component={"span"} noWrap>
                                {item.name}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            }

            return (
                <Link
                    style={{
                        color: theme.palette.greySecondary.main,
                        fontWeight: 500,
                    }}
                    to={onClickName(item)}
                    noWrap
                >
                    {item.name}
                </Link>
            );
        },
        [navigatorMenus, onClickName],
    );

    const getTableValueByColumn = useCallback((columnId, columnType, option) => {
        switch (columnType) {
            case "status":
                return (
                    <Grid container justifyContent={"center"}>
                        <StatusBadge status={option?.[columnId]} />
                    </Grid>
                );
            case "name":
                return (
                    <TableCard
                        thumbnail={getNameThumbnail(type, option)}
                        name={{
                            name: onClickName ? (
                                <>{getLinkComponent(type, option)}</>
                            ) : (
                                <Typography variant={"body1_500"} component={"span"} noWrap>
                                    {option?.name}
                                </Typography>
                            ),
                            startIcon: getNameIcon(type, option),
                            endIcon: showAdditionalIcons && type === "asset" && (
                                <Stack direction={"row"} gap={theme.spacing(0.5)} sx={{ alignItems: "center" }}>
                                    {option?.isConnected ? <IconLink size={16} /> : ""}
                                    {option?.myFavorite && (
                                        <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
                                            <IconStar size={16} htmlColor={theme.palette.etc.orange500} />
                                        </Box>
                                    )}
                                    {option?.hasDubbing && (
                                        <Box
                                            display={"flex"}
                                            alignItems={"center"}
                                            justifyContent={"center"}
                                            width={theme.spacing(2.5)}
                                            height={theme.spacing(2.5)}
                                            borderRadius={theme.spacing(2.5)}
                                            bgcolor={"etc.purple400"}
                                            color="secondary.contrastText"
                                        >
                                            <IconVoice size={12} />
                                        </Box>
                                    )}
                                    {option?.encryptions && option?.encryptions.indexOf("DRM") > -1 && (
                                        <Box
                                            display={"flex"}
                                            alignItems={"center"}
                                            justifyContent={"center"}
                                            width={theme.spacing(2.5)}
                                            height={theme.spacing(2.5)}
                                            borderRadius={theme.spacing(2.5)}
                                            bgcolor={"warning.main"}
                                            color={theme.palette.constant.white}
                                        >
                                            <IconDrm size={12} />
                                        </Box>
                                    )}
                                    {option?.isAnalyzed && (
                                        <Box
                                            display={"flex"}
                                            alignItems={"center"}
                                            justifyContent={"center"}
                                            width={theme.spacing(2.5)}
                                            height={theme.spacing(2.5)}
                                            borderRadius={theme.spacing(2.5)}
                                            bgcolor={"success.light"}
                                            color="success.contrastText"
                                        >
                                            <IconAi size={12} />
                                        </Box>
                                    )}
                                    {option?.isOwnerLocked && (
                                        <Box
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                display: `flex`,
                                                alignItems: `center`,
                                                justifyContent: `center`,
                                                borderRadius: 3.75,
                                                color: theme.palette.common.white,
                                                backgroundColor: theme.palette.etc.gray300,
                                            }}
                                        >
                                            <IconLock size={12} />
                                        </Box>
                                    )}
                                </Stack>
                            ),
                        }}
                        {...(type === "share_asset" && {
                            subName: {
                                name: (
                                    <Grid
                                        container
                                        flexDirection={"row"}
                                        sx={{
                                            "& span:not(:last-child)": {
                                                pr: theme.spacing(1),
                                                borderRight: `solid 1px ${theme.palette.secondary[100]}`,
                                            },
                                            "& span:not(:first-of-type)": {
                                                pl: theme.spacing(1),
                                            },
                                        }}
                                    >
                                        <Typography
                                            variant={"body3_400"}
                                            color={theme.palette.greySecondary[600]}
                                            component={"span"}
                                        >
                                            {option.extensions}
                                        </Typography>
                                        <Typography
                                            variant={"body3_400"}
                                            color={theme.palette.greySecondary[600]}
                                            component={"span"}
                                        >
                                            {convertToByte(option.size)}
                                        </Typography>
                                    </Grid>
                                ),
                            },
                        })}
                    />
                );
            case "size":
                return (
                    <Grid item>
                        <Typography variant={"body1_400"} component={"span"}>
                            {option?.[columnId] ? convertToByte(option?.[columnId]) : "-"}
                        </Typography>
                    </Grid>
                );

            case "count":
                return (
                    <Grid item>
                        <BadgeCollection totalCount={option?.[columnId]} />
                    </Grid>
                );
            case "date": {
                const date = option?.[columnId]?.at
                    ? convertUtcToLocal(option?.[columnId]?.at, "YYYY-MM-DD HH:mm")
                    : "-";
                return <Grid item>{date}</Grid>;
            }
            case "user": {
                const byName = option?.[columnId]?.by
                    ? option?.[columnId]?.by?.name?.length > 8
                        ? `${option?.[columnId]?.by?.name?.slice(0, 8)}...`
                        : option?.[columnId]?.by?.name
                    : "-";
                return (
                    <Grid item>
                        {byName ? (
                            <Button
                                variant={"text"}
                                color={`inherit`}
                                size={"small"}
                                sx={{
                                    minWidth: 0,
                                    textAlign: `left`,
                                }}
                                onClick={(e) => handleUserProfileClick(e, option?.[columnId]?.by?.id)}
                            >
                                {byName}
                            </Button>
                        ) : (
                            "-"
                        )}
                    </Grid>
                );
            }
            case "expireDate": {
                const expiresDate = countDaysBetweenDates({
                    toDate: moment(convertUtcToLocal(option?.[columnId], "YYYY-MM-DD HH:mm:ss")),
                    precise: true,
                });
                if (!expiresDate) return "-";

                return (
                    <Grid container alignItems={"flex-start"} flexDirection={"column"}>
                        <Typography variant={"body1_400"}>{moment(option?.[columnId]).format("YYYY-MM-DD")}</Typography>
                        <Typography variant={"body1_400"}>
                            (
                            {expiresDate >= 0 ? (
                                <>
                                    <Typography variant={"body1_400"} color={theme.palette.warning[600]}>
                                        {expiresDate}
                                    </Typography>
                                    {t(`common::label::${"days left"}`, `days left`)}
                                </>
                            ) : (
                                <>
                                    <Typography variant={"body1_400"} color={theme.palette.error[600]}>
                                        {t(`common::label::${"Expired"}`, `Expired`)}
                                    </Typography>
                                </>
                            )}
                            )
                        </Typography>
                    </Grid>
                );
            }
            case "project":
                return (
                    <Grid item>
                        <Typography variant={"body1_400"} component={"span"}>
                            {option?.[columnId]?.name ?? "-"}
                        </Typography>
                    </Grid>
                );
            case "text":
                return (
                    <Grid item>
                        <Typography variant={"body1_400"} component={"span"}>
                            {option?.[columnId] ?? "-"}
                        </Typography>
                    </Grid>
                );
            case "downloadsRemaining":
                return (
                    <Grid item>
                        <Typography variant={"body1_400"} component={"span"}>
                            {option?.option?.maxDownloads
                                ? option.option.maxDownloads - (option.option.downloadCount ?? 0)
                                : "-"}
                        </Typography>
                    </Grid>
                );
            default:
                return option?.[columnId] ?? null;
        }
    }, []);

    if (data?.length > 0)
        return (
            <TableContainer sx={{ overflow: "auto" }}>
                <Table
                    stickyHeader
                    shadowHeader
                    borderHeader
                    sx={{
                        tableLayout: "fixed",
                        borderTop: `1px solid ${theme.palette.greySecondary[100]}`,
                        minWidth: `${tableMinWidth}px`,
                    }}
                >
                    {showColumns && (
                        <TableHead>
                            <TableRow>
                                {onClickCheck && <TableCell sx={{ width: "32px", px: 0 }} />}
                                {columns.map((column, index) => (
                                    <TableCell
                                        key={`${keyPrefix}-header-cell-${index}`}
                                        sx={{
                                            ...column.style?.custom,
                                        }}
                                    >
                                        {t(`common::label::${column.label}`, column.label)}
                                    </TableCell>
                                ))}
                                <TableCell sx={actionButtonProps ? actionButtonProps : { width: 50 }} />
                            </TableRow>
                        </TableHead>
                    )}
                    <TableBody>
                        {data?.map((item, index) => (
                            <React.Fragment key={`${keyPrefix}-${item.id}`}>
                                <TableRow
                                    hover
                                    role="checkbox"
                                    onClick={() => onClickRow(item)}
                                    selected={!!selected?.find(({ id }) => id === item.id)}
                                    sx={{
                                        ...(onClickRow && {
                                            "&:hover": {
                                                backgroundColor: alpha(theme.palette.greySecondary[50], 0.24),
                                            },
                                            ...(clicked?.id === item.id && {
                                                backgroundColor: `${alpha(
                                                    theme.palette.greySecondary.A100,
                                                    0.36,
                                                )} !important`,
                                            }),
                                        }),
                                    }}
                                >
                                    {onClickCheck && (
                                        <TableCell sx={{ width: "32px", px: 0 }}>
                                            <Checkbox
                                                color={"greySecondary"}
                                                checked={!!selected?.find(({ id }) => id === item.id)}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => onClickCheck(item, e.target.checked)}
                                            />
                                        </TableCell>
                                    )}
                                    {columns.map((column, index) => (
                                        <TableCell
                                            key={`${keyPrefix}-body-cell-${item.id}-${column.id}-${column.type}`}
                                            sx={{
                                                ...column.style?.custom,
                                                paddingLeft: onClickCheck && column.id === "name" ? "0px" : undefined,
                                                flex: column.id === "name" ? 1 : 0,
                                            }}
                                        >
                                            {getTableValueByColumn(column.id, column.type, item)}
                                        </TableCell>
                                    ))}
                                    <TableCell sx={actionButtonProps ? actionButtonProps : { width: "50px" }}>
                                        {actionButton && actionButton(item)}
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
                {!userProfilePending && (
                    <UserDetailPopper
                        id={openUserProfile ? "user-profile-popover" : undefined}
                        handleUserProfileClose={handleUserProfileClose}
                        userProfileAnchorEl={userProfileAnchorEl}
                        openUserProfile={openUserProfile}
                        userProfileData={userProfileData}
                    />
                )}
            </TableContainer>
        );

    return null;
};

export default TableList;
